import { HashTagType } from "@config/hashtags-config";
import { useLocale } from "@translations/locale-context";
import { Button } from "@ui/components/button/button";
import { TextLink } from "@ui/components/text-link/text-link";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import React, { Fragment, HTMLAttributes, memo } from "react";

interface SidebarSectionProps extends HTMLAttributes<HTMLDivElement> {
    sidebarContent: string | HashTagType[];
    title?: string;
}

const Section = memo<SidebarSectionProps>(({ className, sidebarContent, title }) => {
    const { t } = useTranslation();
    const locale = useLocale();

    return (
        <div className={className}>
            {title && <h2 className="ui-typo-overline-small opacity-50">{title}</h2>}
            {typeof sidebarContent === "string" ? (
                <p className="ui-typo-body mt-3">{sidebarContent}</p>
            ) : (
                <div className="ui-typo-body flex flex-wrap text-uxfOrange">
                    {sidebarContent.map((item, index) => (
                        <Fragment key={index}>
                            {item.href ? (
                                <Link href={item.href[locale]} passHref legacyBehavior>
                                    <TextLink variant="orange" className="mr-3 mt-3">
                                        #{t(item.title)}
                                    </TextLink>
                                </Link>
                            ) : (
                                <p className="mr-3 mt-3">#{t(item.title)}</p>
                            )}
                        </Fragment>
                    ))}
                </div>
            )}
        </div>
    );
});

Section.displayName = "Section";

export interface SidebarProps {
    buttonUrl?: string;
    buttonText?: string;
    firstRelease?: string;
    industry?: string;
    project?: string;
    release?: string;
    services?: HashTagType[];
    hashtags?: HashTagType[];
    inCompanySince?: string;
    position?: string;
}

export const Sidebar = memo<SidebarProps>((props) => {
    const {
        buttonUrl,
        firstRelease,
        industry,
        project,
        release,
        services,
        inCompanySince,
        position,
        buttonText,
        hashtags,
    } = props;
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-10 sm:grid sm:min-w-[300px] sm:grid-cols-2 xl:block xl:max-w-xs xl:space-y-10">
            {project && <Section sidebarContent={project} title={t("blog.sidebar.project")} />}
            {industry && (
                <Section
                    className="col-start-2 col-end-3 row-start-2 row-end-3 md:col-auto md:row-auto"
                    sidebarContent={t(industry)}
                    title={t("blog.sidebar.industry")}
                />
            )}
            {services && <Section sidebarContent={services} title={t("blog.sidebar.services")} />}
            {firstRelease && <Section sidebarContent={t(firstRelease)} title={t("blog.sidebar.firstRelease")} />}
            {release && <Section sidebarContent={t(release)} title={t("blog.sidebar.release")} />}
            {inCompanySince && <Section sidebarContent={t(inCompanySince)} title={t("blog.sidebar.inCompanySince")} />}
            {position && <Section sidebarContent={t(position)} title={t("blog.sidebar.position")} />}
            {hashtags && <Section sidebarContent={hashtags} />}
            {buttonUrl && (
                <Button
                    className="w-full md:w-4/5"
                    href={buttonUrl}
                    outlined
                    rel="noreferrer noopener"
                    target="_blank"
                    variant="violet"
                >
                    {buttonText ?? t("blog.sidebar.button")}
                </Button>
            )}
        </div>
    );
});

Sidebar.displayName = "Sidebar";
